import { AnalyticsBrowser } from "@segment/analytics-next";
import { type CookieValue } from "vanilla-cookieconsent";

// Documentation
// https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/
// Chrome extension for debugging
// https://chromewebstore.google.com/detail/segment-inspector/jfcbmnpfbhhlhfclmiijpldieboendfo
export const analytics = new AnalyticsBrowser();

export const loadAnalytics = () =>
  analytics.load({
    writeKey: import.meta.env.VITE_SEGMENT_WRITE_KEY as string,
  });

export const EventEnum = {
  ASSET_DOWNLOADED: "Asset Downloaded",
  ADDED_TO_CART: "Added to Cart",
  CHECKOUT_STARTED: "Checkout Started",
  USER_LOGGED_IN: "User Logged In",
  MARKETPLACE_FILTER_SELECTED: "Marketplace Filter Selected",
  STRIPE_CONNECT_ONBOARDING_STARTED: "Stripe Connect Onboarding Started",
  STRIPE_CONNECT_ONBOARDING_EXITED: "Stripe Connect Onboarding Exited",
  STRIPE_CONNECT_ONBOARDING_STEP_CHANGED:
    "Stripe Connect Onboarding Step Changed",
  PRERELEASE_NOTIFY: "Pre-Release Notify",
  PERSONA_SELECTED: "Persona Selected",
  SINGLE_TRACK_TIME_TO_PUBLISH: "Single Track Time to Publish",
  SHARE_MENU_OPENED: "Share Menu Opened",
  SHARE_CREATED: "Share Created",
} as const;

function handleConsentUpdate(categories: string[]) {
  const hasAnalytics = categories.includes("analytics");

  toggleAnalytics(hasAnalytics);
}

const toggleAnalytics = (enabled: boolean) => {
  if (enabled) {
    enableAnalytics();
  } else {
    disableAnalytics();
  }
};

const enableAnalytics = () => {
  enableGoogleAnalytics(true);
  loadAnalytics();
};

const disableAnalytics = () => {
  enableGoogleAnalytics(false);

  // Disable Segment tracking
  if (analytics.instance) {
    analytics.instance.track = () => Promise.resolve(null as never);
    analytics.instance.page = () => Promise.resolve(null as never);
    analytics.instance.identify = () => Promise.resolve(null as never);
  }
};

const enableGoogleAnalytics = (enabled = true) => {
  window.gtag?.("consent", "update", {
    analytics_storage: enabled ? "granted" : "denied",
    ad_storage: enabled ? "granted" : "denied",
    ad_user_data: enabled ? "granted" : "denied",
    ad_personalization: enabled ? "granted" : "denied",
  });
};

export const COOKIE_CONSENT_CONFIG = {
  categories: {
    necessary: {
      enabled: true,
      readOnly: true, // Cannot be disabled by user
    },
    analytics: {
      enabled: false,
      readOnly: false,
    },
  },
  language: {
    default: "en",
    translations: {
      en: {
        consentModal: {
          title: "We use cookies",
          label: "Cookie Consent",
          description:
            "We use cookies to run this site and, with your consent, to measure performance and improve your experience. You can change your choices anytime.",
          acceptAllBtn: "Accept all",
          acceptNecessaryBtn: "Reject all",
          showPreferencesBtn: "Manage preferences",
          footer: `<a href="/privacy" target="_blank">Privacy Policy</a>`,
        },
        preferencesModal: {
          title: "Cookie Preferences",
          sections: [
            {
              title: "Strictly Necessary Cookies",
              description:
                "Required to run the site and keep it secure (e.g., sign-in and fraud prevention).",
              linkedCategory: "necessary",
            },
            {
              title: "Analytics & Performance",
              description:
                "Helps us understand how the site is used so we can improve it.",
              linkedCategory: "analytics",
            },
          ],
        },
      },
    },
  },
  onConsent: ({ cookie }: { cookie: CookieValue }) => {
    handleConsentUpdate(cookie.categories);
  },
  onChange: ({ cookie }: { cookie: CookieValue }) => {
    handleConsentUpdate(cookie.categories);
  },
};
